import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export function useEvents(storeCode) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await api.get("/events", { params: storeCode ? { store_code: storeCode } : {} });
      setEvents(data);
    } catch {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, [storeCode]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const err = (e, m) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || m);

  const addEvent = async (event) => {
    try {
      const { data } = await api.post("/events", { ...event, store_code: storeCode || event.store_code || null });
      setEvents((p) => [...p, data]);
      toast.success("Event created");
      return data;
    } catch (e) { err(e, "Failed to add event"); throw e; }
  };

  const updateEvent = async (id, updates) => {
    try {
      const existing = events.find((x) => x.id === id) || {};
      const payload = { ...existing, ...updates };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      const { data } = await api.put(`/events/${id}`, payload);
      setEvents((p) => p.map((x) => (x.id === id ? data : x)));
      toast.success("Event updated");
      return data;
    } catch (e) { err(e, "Failed to update event"); throw e; }
  };

  const deleteEvent = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      setEvents((p) => p.filter((x) => x.id !== id));
      toast.success("Event deleted");
    } catch (e) { err(e, "Failed to delete event"); throw e; }
  };

  return { events, loading, addEvent, updateEvent, deleteEvent, refetch: fetchEvents };
}
